import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { CollectedRowReleaseFragmentDoc, getFragment } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { useStableCallback } from '@hooks/useStableCallback';
import { homePlaylist, setUpNextTrackIds } from '@store/audio/AudioQueue';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectedRowRelease on CollectedRelease {
    id
    nftsCount
    release {
      id
      title
      type
      webappUri
      artist {
        id
        name
        webappUri
      }
      track {
        id
      }
      listeningParty
      ...ReleasePlayButton
    }
  }
`);

export const CollectedRow = ({
  collectedRowData,
  getNextTrackIds,
}: {
  collectedRowData: FragmentType<CollectedRowReleaseFragmentDoc>;
  getNextTrackIds: () => string[];
}) => {
  const { release, nftsCount } = getFragment(CollectedRowReleaseFragmentDoc, collectedRowData);

  const { artist, type, track, title, webappUri: releaseWebappUri } = release;
  const { onPlayClick, isPlaying } = usePlayButton({
    isAlbum: type === 'ALBUM',
    trackId: track.id,
    playlist: homePlaylist,
    listeningParty: release.listeningParty,
    releaseId: release.id,
  });

  const playAndavoidBubblingEvent = useStableCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();

    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  return (
    <div
      className="group flex cursor-pointer items-center justify-between overflow-hidden rounded-md px-2 py-1.5 first:pb-1.5 last:pt-1.5 hover:bg-base50"
      onClick={playAndavoidBubblingEvent}
    >
      <div
        className={clsx(
          'flex items-center gap-2 [&_.playButton]:group-hover:opacity-100 [&_.playButton]:group-hover:transition-opacity [&_.playButton]:group-hover:duration-100',
          isPlaying && '[&_.playButton]:opacity-100',
        )}
      >
        <ReleasePlayButton playButtonSize="m" releaseFrag={release} />
        <div className="flex flex-col items-start gap-1 ">
          <AccessibleLink pathnameUri={webappUriPath(releaseWebappUri)}>
            <h2 className="max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-xxs font-medium text-black md:max-w-[200px]">
              {title}
            </h2>
          </AccessibleLink>
          <AccessibleLink pathnameUri={webappUriPath(artist.webappUri)} className="overflow-hidden">
            <h3 className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s font-normal text-base700">
              {artist.name}
            </h3>
          </AccessibleLink>
        </div>
      </div>
      {!!nftsCount && (
        <div className="max-w-[200px] flex-shrink-0 font-base text-base-s text-base700">
          {nftsCount}{' '}
          {getSingularOrPlural({
            count: nftsCount,
            word: 'edition',
          })}
        </div>
      )}
    </div>
  );
};
